import React from "react";

export enum ProductStatus {
  /** The product is ready to sell and can be published to sales channels and apps. Products with an active status aren't automatically published to sales channels, such as the online store, or apps. By default, existing products are set to active. */
  Active = "ACTIVE",
  /** The product is no longer being sold and isn't available to customers on sales channels and apps. */
  Archived = "ARCHIVED",
  /** The product isn't ready to sell and is unavailable to customers on sales channels and apps. By default, duplicated and unarchived products are set to draft. */
  Draft = "DRAFT",
  /** The product is active but you need a direct link to view it. The product doesn't show up in search, collections, or product recommendations. It will be returned in Storefront API and Liquid only when referenced individually by handle, id, or metafield reference.This status is only visible from 2025-10 and up, is translated to active in older versions and can't be changed from unlisted in older versions. */
  Unlisted = "UNLISTED",
}

export type ProductPickerFilterType =
  | "product_type"
  | "vendor"
  | "tag"
  | "status"
  | "collection_id";

export interface ProductPickerState {
  products: ProductPickerProduct[];
  productCount?: number;
  selectedProducts: ProductPickerSelectedProduct[];
  selectedProductsLoading: boolean;
  selectingAllProducts: boolean;
  search: string;
  filters: Record<ProductPickerFilterType, string[]>;
  data?: ProductPickerData;
}

export type ProductFilters = Record<ProductPickerFilterType, string>;

export interface BaseProduct {
  id: string;
  title: string;
  image: string | undefined;
  handle: string;
  requiresSellingPlan: boolean | undefined;
  isBundle?: boolean;
  selectedVariantIds?: string[];
  totalVariantCount?: number;
}

export interface ProductPickerProduct extends BaseProduct {
  status?: ProductStatus;
  hasVariantsThatRequiresComponents: boolean;
  sellingPlanGroupCount: number;
  tags?: string[] | undefined;
  variants?: ProductPickerProductVariant[];
  options?: { name: string; values: string[] }[];
}

export type ProductPickerSelectedProduct = BaseProduct;

export interface ProductPickerProductVariant {
  id: string;
  title: string;
  price: number;
  compareAtPrice: number | null;
  availableForSale: boolean;
  image?: string | null;
  selectedOptions: { name: string; value: string }[];
}

export interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | undefined;
}

export type GetProductPickerInfo = (
  product: ProductPickerSelectedProduct
) => ProductPickerInfo;

export interface ProductPickerInfo {
  isEnabled: boolean;
  customDisabledTooltipMessage: React.ReactNode | null;
  customEnabledTooltipMessage: React.ReactNode | null;
  adminDisable: boolean;
}

/**
 * What any caller may pre-apply when it opens the picker. `filters` become the
 * picker's ordinary filters (the merchant can clear them like any other), and
 * `excludedProductIds` are product GIDs left out of the list.
 */
export interface ProductPickerSeed {
  filters?: Partial<Record<ProductPickerFilterType, string[]>>;
  excludedProductIds?: string[];
}

export type ProductPickerData = ProductPickerSeed &
  (
    | ProductPickerAgProducts
    | ProductPickerGwp
    | ProductPickerOfferProducts
    | ProductPickerAnalytics
    | ProductPickerExclusions
    | ProductPickerAgenticLogicCartTest
    | ProductPickerAiUserInput
    | ProductPickerCustomEvents
    | ProductPickerSelectedAnalytics
    | ProductPickerCheckoutBlockUpsell
    | ProductPickerExperienceProductTargeting
    | ProductPickerCartTargeting
    | ProductPickerCartTester
    | ProductPickerOnsiteUpsellAction
    | ProductPickerOnsiteUpsellCartVariants
    | ProductPickerOnsiteUpsellPdpProducts
  );

export type ProductPickerType = ProductPickerData["type"];

export interface ProductPickerGwp {
  type: "gwp";
  variationId: string;
  tierIndex: number;
}

export interface ProductPickerOfferProducts {
  type: "offerProducts";
  variationId: string;
}

export interface ProductPickerAgProducts {
  type: "agProducts";
}

export interface ProductPickerAnalytics {
  type: "analytics";
}

export interface ProductPickerExclusions {
  type: "exclusions";
}

// The Atlas rail's cart-test ask: pick the products a test cart is built from.
export interface ProductPickerAgenticLogicCartTest {
  type: "agenticLogicCartTest";
}

// An AI chat's parked `request-user-input` question of the `shopify-pick`
// kind, answered by picking products (see `ShopifyProductPickerDialog`).
export interface ProductPickerAiUserInput {
  type: "aiUserInput";
}

export interface ProductPickerCustomEvents {
  type: "customEvents";
}

export interface ProductPickerSelectedAnalytics {
  type: "selectedAnalyticsProducts";
}

export interface ProductPickerCheckoutBlockUpsell {
  type: "checkoutUpsellProductAction";
  checkoutBlockId: string;
  targetingId: string;
  expressionsIndex: number;
}

export interface ProductPickerExperienceProductTargeting {
  type: "experienceProductTargeting";
  expressionsListIndex: number;
  expressionsIndex: number;
}

export interface ProductPickerCartTester {
  type: "cartTester";
  variationId: string;
}

export interface ProductPickerCartTargeting {
  type: "cartTargeting";
  expressionsListIndex: number;
  expressionsIndex: number;
  targetingId: string;
}

// Onsite-upsell config panel writes back into the experienceBuilder component
// draft by rule/condition index (the draft holds the flat authoring rules).
export interface ProductPickerOnsiteUpsellAction {
  type: "onsiteUpsellAction";
  ruleIndex: number;
}

export interface ProductPickerOnsiteUpsellCartVariants {
  type: "onsiteUpsellCartVariants";
  ruleIndex: number;
  conditionIndex: number;
}

export interface ProductPickerOnsiteUpsellPdpProducts {
  type: "onsiteUpsellPdpProducts";
  ruleIndex: number;
  conditionIndex: number;
}

export type ProductPickerLoadStatus = "saving" | "loading" | undefined;
export type ProductPickerSelectionType = "none" | "partial" | "all";
